// The MODULE palette — one colour per workspace module, exactly as the Figma token sheet writes it
// (40000803:38778). It is what tints a timeline row's icon disc: the glyph at rest, the whole disc
// once the row is hovered or clicked open (states matrix 40007749:354365).
//
// This exists because the colours were copied inline at every call site (the global feed's own
// `colorFor`, the story steps' KIND_STEP, the expanded email thread's THREAD_KIND) and had already
// drifted: an email row was drawn task-green, an event was drawn the email indigo, a note the older
// amber. One map, so a module can never be two colours on two rails.
//
// Statuses are NOT modules and stay in @/lib/dueStatus (`statusTokenColor`) — a row's module says
// what KIND of thing it is, its status says how late it is, and the two are separate facts.
//
// That follow-up is done (#210): ContextPanel's saved-item kinds, ongoingOrigin, the story steps'
// contact glyph and the SmartBox's mode strip all resolve through here now, so the Ongoing section
// draws one colour per module. CONTACT arrived with it — Fatemeh's list (Slack, 2026-08-24) named
// six modules and the map only had five, so a contact was being drawn the event purple.
const MODULE_COLOR = {
  event: '#8A38F5',
  task: '#00BD62',
  research: '#388BFF',
  note: '#EAB308',
  email: '#3741F3',
  contact: '#4B5563',
  // The seventh module (busy-timeline-srv#29): messages and calls from busy-source-srv. The token
  // sheet has no messaging colour yet, so it takes the email indigo — a message thread IS the
  // other kind of conversation — and the glyph is what tells the two apart. Named `messaging`
  // because `message` is the panel's word for an email (ALIAS below) and must keep meaning that.
  messaging: '#3741F3',
}

// The app has three vocabularies for the same six modules (the BFF's "email", the panel's
// "message", the icon names "events"/"tasks"/"notes"/"contacts", the design's "calendar"/
// "web-search"/"reminder"). Every one of them lands on the same colour rather than on the fallback.
const ALIAS = {
  calendar: 'event',
  events: 'event',
  tasks: 'task',
  reminder: 'note',
  notes: 'note',
  message: 'email',
  emails: 'email',
  mail: 'email',
  search: 'research',
  websearch: 'research',
  'web-search': 'research',
  web: 'research',
  global: 'research',
  // A Research PACK is the research module's own thing (Pouria, 2026-10-01, frame
  // 40011046:161537 draws "Best sun hat for travel" in the research globe, research blue):
  // busy-timeline-srv#42 names an entity offer's module `researchpack`, and the app's own
  // spellings of the same noun land here too, rather than on the fallback.
  researchpack: 'research',
  researchpacks: 'research',
  'research-pack': 'research',
  'research-packs': 'research',
  research_pack: 'research',
  contacts: 'contact',
  person: 'contact',
  people: 'contact',
  // The message sources (busy-timeline-srv#29), and the box's own mode name for the call log.
  sms: 'messaging',
  whatsapp: 'messaging',
  call: 'messaging',
  calls: 'messaging',
  messages: 'messaging',
}

// The canonical module name for any of the vocabularies above; '' for anything unrecognised, so a
// caller can tell "not a module" from "a module we have no colour for".
export function moduleKey(type) {
  const key = String(type || '').trim().toLowerCase()
  const canon = ALIAS[key] || key
  return MODULE_COLOR[canon] ? canon : ''
}

// The module's colour. Anything unrecognised reads as an event — the same fallback the global feed's
// icon lookup has always used, so an unknown row is drawn rather than dropped.
export function moduleColor(type) {
  return MODULE_COLOR[moduleKey(type)] || MODULE_COLOR.event
}

// A copy for callers that need the whole map (never the map itself — nothing may edit the palette).
export function moduleColors() {
  return { ...MODULE_COLOR }
}
